import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";

/**
 * Theme system — 5 manual themes + 1 seasonal "auto" mode.
 *
 *   light      — default (white surface, ink-black accent)
 *   dark       — existing near-black surface
 *   ocean      — deep blue, navy ink
 *   forest     — fresh green, mossy ink
 *   sunset     — warm coral / dusty orange
 *   midnight   — deep purple / indigo
 *   seasonal   — auto rotates based on current month:
 *                  Dec-Feb → midnight (winter)
 *                  Mar-May → forest (spring)
 *                  Jun-Aug → sunset (summer)
 *                  Sep-Nov → ocean (autumn)
 *
 * Implementation: each theme owns a CSS class on <html>. We toggle
 * exactly one `theme-<name>` class at a time. The `dark` class also
 * gets added for any palette that needs dark-mode styling so existing
 * Tailwind `dark:` utilities keep working.
 */

const THEME_KEY = "sf-theme";
const VALID_THEMES = ["light", "dark", "ocean", "forest", "sunset", "midnight", "seasonal"];
const DARK_THEMES = new Set(["dark", "midnight", "ocean"]);

function seasonalPick(now = new Date()) {
  // northern-hemisphere seasons keyed off month number (1-12)
  const m = now.getMonth() + 1;
  if (m === 12 || m <= 2) return "midnight"; // winter
  if (m <= 5) return "forest"; // spring
  if (m <= 8) return "sunset"; // summer
  return "ocean"; // autumn
}

function resolveTheme(picked) {
  if (picked === "seasonal") return seasonalPick();
  return picked;
}

const ThemeContext = createContext({
  theme: "light",
  resolvedTheme: "light",
  setTheme: () => {},
  toggle: () => {},
});

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => {
    if (typeof window === "undefined") return "light";
    const saved = localStorage.getItem(THEME_KEY);
    return VALID_THEMES.includes(saved) ? saved : "light";
  });

  // When the user picks "seasonal" the actual applied palette can drift
  // (e.g. you cross midnight on the 1st of a season). Recompute every
  // 30 min so the UI keeps in sync without a reload.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (theme !== "seasonal") return;
    const id = setInterval(() => setTick((t) => t + 1), 30 * 60 * 1000);
    return () => clearInterval(id);
  }, [theme]);

  const resolvedTheme = useMemo(() => resolveTheme(theme), [theme, tick]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const root = document.documentElement;
    // strip every theme-* class first so switches are clean
    VALID_THEMES.forEach((t) => root.classList.remove(`theme-${t}`));
    root.classList.add(`theme-${resolvedTheme}`);
    // Keep `dark` class for Tailwind dark: variants on the dark palettes
    if (DARK_THEMES.has(resolvedTheme)) root.classList.add("dark");
    else root.classList.remove("dark");
    // persist what the user PICKED (so seasonal stays seasonal across reloads)
    localStorage.setItem(THEME_KEY, theme);
  }, [theme, resolvedTheme]);

  const setTheme = useCallback((next) => {
    if (VALID_THEMES.includes(next)) setThemeState(next);
  }, []);

  // legacy toggle = light↔dark
  const toggle = useCallback(() => {
    setThemeState((t) => (DARK_THEMES.has(resolveTheme(t)) ? "light" : "dark"));
  }, []);

  const value = useMemo(
    () => ({ theme, resolvedTheme, setTheme, toggle, VALID_THEMES }),
    [theme, resolvedTheme, setTheme, toggle],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export const useTheme = () => useContext(ThemeContext);
export const THEMES = VALID_THEMES;
